import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { ConfigService } from '@venecia/services';
import { UrlBuilder } from '@venecia/builders';
import { ApiResponse, TemplateHelper } from '@venecia/apis/base';

import { Session } from './model/session.model';

@Injectable({ providedIn: 'root' })
export class SessionApi {
    private readonly http = inject(HttpClient);
    private readonly rawHttp = new HttpClient(inject(HttpBackend));
    private readonly configService = inject(ConfigService);

    private urlBuilder?: UrlBuilder;

    protected constructor() {
        this.configService
            .select('http.rest.auth.url')
            .pipe(take(1))
            .subscribe((value: string) => {
                this.urlBuilder = new UrlBuilder(value, '/api/sessions');
            });
    }

    logIn(username: string, password: string): Observable<ApiResponse<Session>> {
        const url = this.urlBuilder!.build('/login');
        const payload = { username, password };

        return this.http.post<ApiResponse<Session>>(url, payload, {
            credentials: 'include',
            withCredentials: true
        })
            .pipe(
                TemplateHelper.applyRetry(),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }

    requestPasswordRecovery(username: string): Observable<ApiResponse<unknown>> {
        const url = this.urlBuilder!.build(`/password/recovery/${encodeURIComponent(username)}`, {
            username,
        });

        return this.http.post<ApiResponse<unknown>>(url, null, {
            credentials: 'include',
            withCredentials: true,
        })
            .pipe(
                TemplateHelper.applyRetry(),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }

    validateOtp(otpCode: string, sessionKey: string): Observable<ApiResponse<unknown>> {
        const url = this.urlBuilder!.build('/otp/validate', { sessionKey });
        const payload = {
            'otp-code': otpCode,
        };

        return this.http.put<ApiResponse<unknown>>(url, payload, {
            credentials: 'include',
            withCredentials: true,
        })
            .pipe(
                TemplateHelper.applyRetry(),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }

    refreshOtpCode(sessionKey: string): Observable<ApiResponse<Session>> {
        const url = this.urlBuilder!.build('/otp/refresh-code', { sessionKey });

        return this.http.post<ApiResponse<Session>>(url, null, {
            credentials: 'include',
            withCredentials: true,
        })
            .pipe(
                TemplateHelper.applyRetry(),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }

    changePassword(newValue: string, accessToken: string): Observable<unknown> {
        const url = this.urlBuilder!.build('/password/change');
        return this.rawHttp.put<unknown>(url, {
            'new-value': newValue,
        }, this.flowOptions(accessToken))
            .pipe(
                TemplateHelper.applyRetry(),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }

    verifyEmail(newValue: string, accessToken: string): Observable<unknown> {
        const url = this.urlBuilder!.build('/email/verify');
        return this.rawHttp.put<unknown>(url, {
            'new-value': newValue,
        }, this.flowOptions(accessToken))
            .pipe(
                TemplateHelper.applyRetry(),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }

    private flowOptions(accessToken: string) {
        return {
            withCredentials: true,
            headers: new HttpHeaders({
                Authorization: `Bearer ${accessToken}`,
            }),
        };
    }
}
